import { ParseKeysByKeyPrefix } from 'i18next'
import { AlertTriangle, CheckCircle2, Clock, FileText, type LucideIcon, Package, Plane, RotateCcw, Truck, XCircle } from 'lucide-react'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useParcelStatus } from '@/api/shipping/queries/useParcelStatus'
import type { ParcelStatus } from '@/api/shipping/types/ParcelStatus'
import { AppDialog } from '@/components/AppDialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { formatDateEu } from '@/helpers/formatDate'
import { cn } from '@/lib/utils'

interface Props {
  toTrackingNumber: string
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

/** Map Exelot status codes to icons (text comes from translations). */
const STATUS_ICONS: Record<string, LucideIcon> = {
  // shop / info received
  '00': Clock,
  '01': FileText,
  '05': Package,
  // first mile
  '10': Package,
  '11': AlertTriangle,
  '12': Package,
  '13': Package,
  '17': FileText,
  '114': Package,
  // forwarder / flight
  '14': Package,
  '15': Plane,
  '38': Plane,
  '74': AlertTriangle,
  '77': AlertTriangle,
  '78': AlertTriangle,
  // customs
  '32': FileText,
  '324': FileText,
  '329': CheckCircle2,
  '35': FileText,
  '350': FileText,
  '40': AlertTriangle,
  '400': AlertTriangle,
  '408': CheckCircle2,
  '409': CheckCircle2,
  '41': XCircle,
  '42': AlertTriangle,
  '45': Plane,
  '47': AlertTriangle,
  // central warehouse
  '23': AlertTriangle,
  '34': RotateCcw,
  '36': Package,
  '363': Truck,
  '369': Truck,
  '37': XCircle,
  '39': Package,
  '390': Package,
  '48': Package,
  '49': RotateCcw,
  // last mile main
  '20': Truck,
  '21': Package,
  '210': Package,
  '211': Package,
  '215': Truck,
  '218': Package,
  '22': Truck,
  '24': Truck,
  '25': AlertTriangle,
  '27': AlertTriangle,
  '29': RotateCcw,
  '33': AlertTriangle,
  // last mile secondary
  '43': Package,
  '430': Package,
  '431': Package,
  '432': Truck,
  '435': Package,
  '44': RotateCcw,
  // last mile endpoint
  '50': Truck,
  '51': CheckCircle2,
  '52': CheckCircle2,
  '53': Clock,
  '55': Clock,
  '60': RotateCcw,
  '68': AlertTriangle,
  '69': Clock,
  '690': AlertTriangle,
  '691': AlertTriangle,
  '692': AlertTriangle,
  '693': AlertTriangle,
  '694': AlertTriangle,
  '699': Clock,
  // generic
  '31': AlertTriangle,
  '46': Truck,
  '54': XCircle,
  '64': AlertTriangle,
  '65': AlertTriangle,
  '66': AlertTriangle,
  '67': AlertTriangle,
  '70': AlertTriangle,
  '71': AlertTriangle,
  '72': Package,
  '73': XCircle,
  '75': RotateCcw,
  '76': RotateCcw,
  '79': AlertTriangle,
  '81': FileText,
  '90': CheckCircle2,
  '91': RotateCcw,
  '94': XCircle,
  '95': XCircle,
  '99': XCircle,
  // returns
  R00: RotateCcw,
  R21: RotateCcw,
  R22: RotateCcw,
  R38: RotateCcw,
  R381: RotateCcw,
  R430: RotateCcw,
  R431: RotateCcw,
  R432: RotateCcw,
  R50: RotateCcw,
  R52: CheckCircle2,
  R67: AlertTriangle,
  R68: AlertTriangle,
  R69: AlertTriangle,
  R73: XCircle,
  R94: XCircle,
  R99: XCircle,
}

type DeliveryStatusCodeKey = ParseKeysByKeyPrefix<'pkg', 'deliveryStatusCodes'>

const getStatusTranslationKey = (status: ParcelStatus, exists: (key: string) => boolean): DeliveryStatusCodeKey | null => {
  const subCode = status.statusSubCode?.trim()
  if (subCode) {
    const specificKey = `pkg.deliveryStatusCodes.${status.code}_${subCode}`
    if (exists(specificKey)) {
      return specificKey as DeliveryStatusCodeKey
    }
  }

  const baseKey = `pkg.deliveryStatusCodes.${status.code}`
  if (exists(baseKey)) {
    return baseKey as DeliveryStatusCodeKey
  }

  return null
}

const formatDate = (dateString: string) => formatDateEu(dateString)

const formatShippingDate = (dateString: string) => formatDateEu(dateString)

export const DeliveryStatus = ({ toTrackingNumber, isOpen, onOpenChange }: Props) => {
  const { t, i18n } = useTranslation()

  const { data: statusData, isLoading, error } = useParcelStatus(toTrackingNumber, isOpen)

  // Statuses come in reverse chronological order (newest first), so reverse them
  const sortedStatuses = useMemo(() => {
    if (!statusData?.statuses) return []
    // Reverse to show chronological order (oldest first)
    return [...statusData.statuses].reverse()
  }, [statusData])

  // Find the current/active status (usually the latest non-delivered status)
  const currentStatusIndex = useMemo(() => {
    if (!sortedStatuses || sortedStatuses.length === 0) return -1

    // Find the delivered status (code 51 or 52)
    const deliveredIndex = sortedStatuses.findIndex(s => s.code === '51' || s.code === '52')

    // If delivered, that's the current one (highlighted)
    if (deliveredIndex !== -1) return deliveredIndex

    // If no delivered status, the last one (most recent) is current
    return sortedStatuses.length - 1
  }, [sortedStatuses])

  // Get shipping date from the earliest status or package
  const shippingDate = useMemo(() => {
    if (sortedStatuses && sortedStatuses.length > 0) {
      // The first status in sorted array is the earliest
      const earliestStatus = sortedStatuses[0]
      return formatShippingDate(earliestStatus.dateTime)
    }
  }, [sortedStatuses])

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className='flex flex-col gap-4 pt-4'>
        {isLoading && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary'>{t('pkg.deliveryStatusLoading')}</p>
            <Spinner className='size-10' />
          </div>
        )}

        {!!error && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary text-center'>{t('pkg.deliveryStatusError')}</p>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              {t('common.close')}
            </Button>
          </div>
        )}

        {!isLoading && !error && statusData && (
          <>
            {/* Shipping Info */}
            <div className='flex flex-col gap-1'>
              <p className='text-text-primary text-base font-medium'>{t('pkg.deliveryStatusTitle')}</p>
              <p className='text-text-secondary text-xs'>
                {t('pkg.deliveryStatusShippedOn', {
                  date: shippingDate,
                  trackingNumber: statusData.parcel.trackingNumber,
                })}
              </p>
            </div>

            {/* Status Timeline */}
            <div className='flex max-h-[400px] flex-col gap-[27px] overflow-y-auto px-2'>
              {sortedStatuses.map((status, index) => {
                const isActive = index === currentStatusIndex
                const isPast = index < currentStatusIndex
                const translationKey = getStatusTranslationKey(status, key => i18n.exists(key))
                const Icon = STATUS_ICONS[status.code] ?? Clock
                const statusDate = formatDate(status.dateTime)
                const subCode = status.statusSubCode?.trim()
                const additionalInfo = status.statusAdditionalInfo?.trim()
                const usedSpecificSub = !!translationKey && !!subCode && String(translationKey).endsWith(`_${subCode}`)
                const showAdditionalInfo = !!translationKey && !!additionalInfo && !usedSpecificSub

                return (
                  <div
                    key={`${status.code}-${subCode ?? ''}-${status.dateTime}`}
                    className={cn('flex items-start justify-end gap-1', isActive && 'bg-button-primary-selected-segment rounded-xl p-2')}
                  >
                    <div className='flex grow flex-col gap-1'>
                      <p className={cn('text-sm', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}>
                        {translationKey ? (
                          <>
                            <Trans i18nKey={translationKey} />
                            {showAdditionalInfo ? ` ${additionalInfo}` : null}
                          </>
                        ) : (
                          status.status
                        )}
                      </p>
                      <p className={cn('text-xs', isActive ? 'text-text-secondary' : 'text-text-disabled', isPast && 'text-text-disabled')}>
                        {statusDate}
                      </p>
                    </div>
                    <Icon
                      className={cn('size-5', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}
                    />
                  </div>
                )
              })}
            </div>

            {/* Delivery Link */}
            <a
              href={`https://myparcel.exelot.com/tracking?barcodes=${statusData.parcel.trackingNumber}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Button variant='link' className='text-text-link hover:text-text-link/80 text-sm underline underline-offset-2'>
                {t('pkg.deliveryStatusFullDetails')}
              </Button>
            </a>
          </>
        )}
      </div>
    </AppDialog>
  )
}
